import React from 'react';
import { CaseStudy } from '../types';

export const smartcertsCaseStudy: CaseStudy = {
  metadata: {
    id: 'smartcerts',
    slug: 'smartcerts',
    template: 1,
    meta: {
      title: 'SmarterCerts by Atini Studio | Atini Studio',
      description: 'Digitizing Calibration with Precision and Ease. Atini Studio partnered with SmarterCerts to build a modern, cloud-based web and mobile application that reimagines how calibration certificates are created, managed, and delivered.',
      canonicalUrl: 'https://atinistudio.com/case-study/smartcerts',
      ogImage: 'https://atinistudio.com/assets/atini-logo-colored.svg',
    },
  },
  data: {
    pillLabel: 'Mobile App | Calibration Tech',
    title: 'SmarterCerts by Atini Studio',
    description: 'Digitizing Calibration with Precision and Ease',
    image: 'smart-certs/main.png',
    imageAlt: 'SmarterCerts App',
    story: {
      title: 'The Story',
      content: 'Atini Studio partnered with SmarterCerts, a forward-thinking calibration technology company, to build a modern, cloud-based web and mobile application that reimagines how calibration certificates are created, managed, and delivered. Aimed at scaling calibration service providers and inspection teams, the platform transforms traditional paper-based workflows into fast, accurate, and fully digital operations—enabling users to generate professional, compliant PDF certificates in just a few taps. Designed from the ground up for scalability and ease of use, SmarterCerts provides a digital-first solution that dramatically improves the efficiency, accuracy, and traceability of calibration work. From mobile technicians working in the field to managers overseeing quality control and compliance, the platform serves as a single source of truth for all things calibration.',
    },
    challenge: {
      title: 'The Challenge',
      content: 'Calibration service providers and inspection teams were struggling with inefficient, error-prone paper-based processes. They needed a modern, cloud-based solution that could digitize certificate generation, enable offline capabilities for field technicians, ensure compliance with ISO and NIST standards, and provide real-time synchronization across all stakeholders.',
    },
    stats: [
      {
        value: 'Cross-Platform',
        label: 'iOS & Android',
        icon: 'phone.svg',
        gradientFrom: 'from-[#FA832E]',
        gradientTo: 'to-[#F5CA09]',
      },
      {
        value: 'Offline Mode',
        label: 'With Sync',
        icon: 'clock.svg',
        gradientFrom: 'from-[#FA832E]',
        gradientTo: 'to-[#F5CA09]',
      },
      {
        value: 'Cloud-Based',
        label: 'Real-Time Sync',
        icon: 'globe.svg',
        gradientFrom: 'from-[#FA832E]',
        gradientTo: 'to-[#F5CA09]',
      },
    ],
    journeySteps: [
      {
        number: 1,
        title: 'Discovery & Research',
        description: 'Conducted extensive research into calibration workflows, interviewed technicians and managers to understand pain points in paper-based processes, and analyzed compliance requirements for ISO and NIST standards.',
        icon: 'discovery_icon.svg',
        alt: 'Discovery',
        items: [
          'Industry research & user interviews',
          'Workflow analysis',
          'Compliance requirements analysis',
        ],
      },
      {
        number: 2,
        title: 'Design & Architecture',
        description: 'Designed intuitive mobile-first interface for field technicians and office managers. Created scalable cloud architecture to support offline capabilities, real-time synchronization, and automated certificate generation.',
        icon: 'design_icon.svg',
        alt: 'Design',
        items: [
          'Mobile-first UI/UX design',
          'Cloud architecture planning',
          'Offline sync strategy',
        ],
      },
      {
        number: 3,
        title: 'Development',
        description: 'Built the platform using React Native for cross-platform mobile support, Node.js for backend logic, and cloud infrastructure for secure data storage and real-time synchronization. Implemented automated certificate generation, barcode scanning, and electronic signatures.',
        icon: 'develop_icon.svg',
        alt: 'Development',
        items: [
          'React Native mobile app',
          'Node.js backend development',
          'Automated certificate generation',
        ],
      },
      {
        number: 4,
        title: 'Launch & Optimization',
        description: 'Launched the platform with beta calibration service providers, gathered feedback from technicians and managers, and optimized workflows based on real-world usage. Ensured seamless offline-to-online synchronization and compliance with regulatory requirements.',
        icon: 'deliver_icon.svg',
        alt: 'Launch',
        items: [
          'Beta testing with service providers',
          'Performance optimization',
          'Compliance validation',
        ],
      },
    ],
    resultsMetrics: [
      {
        label: 'Documentation Time',
        before: 'Manual',
        after: 'Automated',
        change: '90%',
        changeType: '-',
        icon: 'trending_down.svg',
      },
      {
        label: 'Certificate Generation',
        before: 'Paper-Based',
        after: 'Digital',
        change: '100%',
        changeType: '+',
        icon: 'zap.svg',
      },
      {
        label: 'Operational Efficiency',
        before: 'Fragmented',
        after: 'Streamlined',
        change: 'Real-Time',
        changeType: '+',
        icon: 'trending_up.svg',
      },
      {
        label: 'Compliance Accuracy',
        before: 'Error-Prone',
        after: 'Automated',
        change: '99.9%',
        changeType: '+',
        icon: 'earnings.svg',
      },
    ],
    testimonial: {
      name: 'SmarterCerts Team',
      role: 'Calibration Technology Platform',
      quote: 'SmarterCerts fundamentally changes the way calibration businesses operate. By eliminating inefficient, error-prone paper processes and replacing them with intelligent, cloud-powered tools, the app allows companies to scale faster, increase accuracy, and reduce operational overhead. Technicians now spend less time on documentation and more time delivering quality service. Managers gain real-time visibility into operations, assets, and compliance statuses. And customers benefit from faster turnaround and cleaner, more professional digital certificates—all delivered via mobile or desktop. Whether you\'re a small team just starting to digitize or a large enterprise ready to modernize at scale, SmarterCerts provides the infrastructure to help you move faster, stay compliant, and grow with confidence.',
      icon: 'smart-certs/logo.png',
    },
    platformFeatures: [
      {
        title: 'Automated Certificate Generation',
        description: 'An intelligent algorithm powers rapid and accurate creation of PDF calibration certificates, pre-filling values, dates, signatures, and more based on device data and technician input.',
        stat: 'Intelligent automation',
      },
      {
        title: 'Offline Mode with Sync',
        description: 'Technicians can create and store certificates even without internet access. All data is securely synced to the cloud once a connection is restored.',
        stat: 'Always available',
      },
      {
        title: 'Asset & Technician Management',
        description: 'Track asset history, assign tasks to technicians, and maintain detailed service logs across your entire organization.',
        stat: 'Complete visibility',
      },
      {
        title: 'Barcode Scanning & Quick Search',
        description: 'Instantly locate devices or jobs with barcode scanning and intuitive search filters, reducing time spent on paperwork or manual lookups.',
        stat: 'Instant access',
      },
      {
        title: 'Electronic Signatures & Compliance',
        description: 'Digitally sign certificates to maintain audit trails, enhance professionalism, and meet ISO, NIST, or other regulatory requirements.',
        stat: 'Full compliance',
      },
      {
        title: 'Streamlined UI/UX',
        description: 'A redesigned and highly intuitive user experience enables quick onboarding and effortless navigation for technicians in the field or supervisors in the office.',
        stat: 'Intuitive design',
      },
    ],
    technologies: [
      {
        name: 'React Native',
        description: 'Cross-platform mobile app development',
        logo: 'react_native_logo.svg',
      },
      {
        name: 'Node.js',
        description: 'Backend server logic',
        logo: 'tech-icons/nodejs-icon.svg',
      },
      {
        name: 'Cloud Infrastructure',
        description: 'Secure cloud-based data storage and real-time sync',
        logo: 'aws_logo.svg',
      },
    ],
    securityTitle: 'Security & Compliance',
    securityDescription: 'Secure cloud-based data storage, encrypted data handling, electronic signatures for audit trails, compliance with ISO and NIST standards, and real-time synchronization with secure protocols.',
  },
};
